import type {
  OptimizationResult,
  RouteMetrics,
  RouteAlternative,
  DetourRecommendation,
  RouteConstraints,
  DrivingProfile,
  DrivingProfileConfig,
  Stage,
} from '@my-roadtrip/types'
import { DRIVING_PROFILES, FUEL_CONSTANTS } from '@my-roadtrip/types'

/**
 * Route Optimization Service
 * Implements Traveling Salesman Problem (TSP) solver with constraints
 */
import { createLogger } from '../../common/utils/logger.js'
const logger = createLogger('RouteOptimizationService')

export class RouteOptimizationService {
  /**
   * Optimize stage order using greedy nearest-neighbor TSP approximation
   */
  async optimizeRoute(
    stages: Stage[],
    constraints: RouteConstraints
  ): Promise<OptimizationResult> {
    const startTime = Date.now()

    if (stages.length < 2) {
      throw new Error('At least 2 stages required for optimization')
    }

    const originalOrder = stages.map((s) => s.id)
    const profile = DRIVING_PROFILES[constraints.profile]

    // Calculate original metrics
    const originalMetrics = this.calculateRouteMetrics(stages, profile, constraints)

    // Run TSP optimization
    const optimizedStages = this.solveTSP(stages, constraints, profile)
    const optimizedOrder = optimizedStages.map((s) => s.id)

    // Calculate optimized metrics
    const optimizedMetrics = this.calculateRouteMetrics(optimizedStages, profile, constraints)

    // Calculate savings
    const savings = {
      distanceKm: originalMetrics.totalDistanceKm - optimizedMetrics.totalDistanceKm,
      durationHours: originalMetrics.totalDurationHours - optimizedMetrics.totalDurationHours,
      fuelLiters: originalMetrics.fuelLiters - optimizedMetrics.fuelLiters,
      costEur: originalMetrics.fuelCostEur - optimizedMetrics.fuelCostEur,
      co2Kg: originalMetrics.co2Kg - optimizedMetrics.co2Kg,
    }

    // Generate alternative routes
    const alternatives = this.generateAlternatives(stages, profile, constraints)

    const result: OptimizationResult = {
      originalOrder,
      optimizedOrder,
      savings,
      comparison: {
        original: originalMetrics,
        optimized: optimizedMetrics,
      },
      alternatives,
    }

    const computationTime = Date.now() - startTime

    logger.info('Route optimization completed', {
      stages: stages.length,
      profile: constraints.profile,
      computationTimeMs: computationTime,
      distanceKmBefore: originalMetrics.totalDistanceKm,
      distanceKmAfter: optimizedMetrics.totalDistanceKm,
      distanceKmSaved: savings.distanceKm,
      durationHoursBefore: originalMetrics.totalDurationHours,
      durationHoursAfter: optimizedMetrics.totalDurationHours,
      durationHoursSaved: savings.durationHours,
      fuelLitersSaved: savings.fuelLiters,
      costEurSaved: savings.costEur,
      co2KgSaved: savings.co2Kg
    })

    // Internal optimization - no external API cost to track

    return result
  }

  /**
   * Greedy Nearest-Neighbor TSP approximation
   * Time complexity: O(n²)
   * Approximation ratio: ~1.25x optimal (good enough for road trips)
   * 
   * IMPORTANT: Preserves first and last stages (start and end points are fixed)
   * Only optimizes intermediate waypoints
   */
  private solveTSP(
    stages: Stage[],
    constraints: RouteConstraints,
    profile: DrivingProfileConfig
  ): Stage[] {
    if (stages.length <= 2) {
      return [...stages] // No optimization possible/needed
    }

    // Keep first and last stages fixed (start and destination)
    const start = stages[0]
    const end = stages.at(-1)!
    const intermediateStages = stages.slice(1, -1)

    // If only 3 stages total (start + 1 waypoint + end), no optimization needed
    if (intermediateStages.length === 1) {
      return [start, intermediateStages[0], end]
    }

    // Optimize intermediate stages using nearest-neighbor
    const unvisited = new Set(intermediateStages)
    const route: Stage[] = [start] // Start fixed
    let current = start

    // Greedy: always pick nearest unvisited intermediate stage
    while (unvisited.size > 0) {
      let nearest: Stage | null = null
      let minDistance = Infinity

      logger.debug(`   Current: ${current.name || current.id} (${current.location.coordinates.lat.toFixed(2)}, ${current.location.coordinates.lng.toFixed(2)})`)
      logger.debug(`   Evaluating ${unvisited.size} candidates:`)

      for (const candidate of unvisited) {
        const distance = this.calculateDistance(
          current.location.coordinates,
          candidate.location.coordinates
        )

        logger.debug(`     - ${candidate.name || candidate.id}: ${distance.toFixed(1)} km`)

        if (distance < minDistance) {
          minDistance = distance
          nearest = candidate
        }
      }

      if (nearest) {
        logger.debug(`   ✓ Selected: ${nearest.name || nearest.id} (${minDistance.toFixed(1)} km away)`)
        route.push(nearest)
        unvisited.delete(nearest)
        current = nearest
      }
    }

    // End fixed
    route.push(end)

    logger.debug('TSP optimization summary', {
      originalOrder: stages.map(s => s.name || s.id.slice(0, 8)).join(' → '),
      optimizedOrder: route.map(s => s.name || s.id.slice(0, 8)).join(' → '),
      start: start.name || start.id,
      end: end.name || end.id,
      optimizedIntermediateCount: intermediateStages.length
    })

    return route
  }

  /**
   * Calculate route metrics (distance, duration, fuel, cost, CO2)
   */
  private calculateRouteMetrics(
    stages: Stage[],
    profile: DrivingProfileConfig,
    constraints: RouteConstraints
  ): RouteMetrics {
    let totalDistanceKm = 0
    let totalDurationHours = 0

    // Sum distances between consecutive stages
    for (let i = 0; i < stages.length - 1; i++) {
      const from = stages[i].location.coordinates
      const to = stages[i + 1].location.coordinates
      const distanceKm = this.calculateDistance(from, to)

      totalDistanceKm += distanceKm

      // Duration = distance / avg speed
      const drivingHours = distanceKm / profile.avgSpeedKmh

      // Add rest breaks based on profile
      const numRests = Math.floor(distanceKm / profile.restFrequencyKm)
      const restHours = (numRests * profile.restDurationMin) / 60

      totalDurationHours += drivingHours + restHours
    }

    // Calculate fuel consumption
    const baseConsumption = FUEL_CONSTANTS.AVG_CONSUMPTION_L_PER_100KM
    const fuelLiters = (totalDistanceKm / 100) * baseConsumption * profile.fuelEfficiency

    // Calculate costs
    const fuelCostEur = fuelLiters * FUEL_CONSTANTS.FUEL_PRICE_EUR_PER_L
    const tollCostEur = constraints.avoidTolls
      ? 0
      : (totalDistanceKm / 100) * FUEL_CONSTANTS.TOLL_EUR_PER_100KM

    // Calculate CO2 emissions
    const co2Kg = fuelLiters * FUEL_CONSTANTS.CO2_KG_PER_L

    return {
      totalDistanceKm: Math.round(totalDistanceKm * 10) / 10,
      totalDurationHours: Math.round(totalDurationHours * 10) / 10,
      fuelLiters: Math.round(fuelLiters * 10) / 10,
      fuelCostEur: Math.round(fuelCostEur * 100) / 100,
      co2Kg: Math.round(co2Kg * 10) / 10,
      tollCostEur: tollCostEur > 0 ? Math.round(tollCostEur * 100) / 100 : undefined,
    }
  }

  /**
   * Generate alternative route options
   */
  private generateAlternatives(
    stages: Stage[],
    profile: DrivingProfileConfig,
    constraints: RouteConstraints
  ): RouteAlternative[] {
    const alternatives: RouteAlternative[] = []

    // Alternative 1: Scenic route (prioritize nature/views)
    if (constraints.preferScenic) {
      const scenicStages = this.prioritizeScenic(stages)
      const scenicMetrics = this.calculateRouteMetrics(scenicStages, profile, constraints)

      alternatives.push({
        id: 'scenic',
        name: 'Route panoramique',
        order: scenicStages.map((s) => s.id),
        metrics: scenicMetrics,
        description: 'Privilégie les paysages et points de vue naturels',
      })
    }

    // Alternative 2: Fastest (different profile)
    if (profile.id !== 'fast') {
      const fastProfile = DRIVING_PROFILES.fast
      const fastMetrics = this.calculateRouteMetrics(stages, fastProfile, constraints)

      alternatives.push({
        id: 'fastest',
        name: 'Plus rapide',
        order: stages.map((s) => s.id),
        metrics: fastMetrics,
        description: `Avec profil ${fastProfile.name} (${fastProfile.avgSpeedKmh} km/h)`,
      })
    }

    // Alternative 3: Most economical
    if (profile.id !== 'eco') {
      const ecoProfile = DRIVING_PROFILES.eco
      const ecoMetrics = this.calculateRouteMetrics(stages, ecoProfile, constraints)

      alternatives.push({
        id: 'economical',
        name: 'Plus économique',
        order: stages.map((s) => s.id),
        metrics: ecoMetrics,
        description: `Avec profil ${ecoProfile.name} (-15% consommation)`,
      })
    }

    return alternatives
  }

  /**
   * Prioritize scenic stages (nature, viewpoints)
   */
  private prioritizeScenic(stages: Stage[]): Stage[] {
    // Simple heuristic: stages with "nature", "vue", "panorama" in notes
    // In production, would use ML model or external scenic route API
    return [...stages].sort((a, b) => {
      const aScenic = this.getScenicScore(a)
      const bScenic = this.getScenicScore(b)
      return bScenic - aScenic
    })
  }

  private getScenicScore(stage: Stage): number {
    const notes = stage.notes?.toLowerCase() || ''
    let score = 0

    if (notes.includes('nature')) score += 3
    if (notes.includes('vue')) score += 3
    if (notes.includes('panorama')) score += 3
    if (notes.includes('montagne')) score += 2
    if (notes.includes('mer')) score += 2
    if (notes.includes('lac')) score += 2
    if (notes.includes('forêt')) score += 1

    return score
  }

  /**
   * Suggest intelligent detours (POIs near route)
   */
  async suggestDetours(
    stages: Stage[],
    pois: Array<{ id: string; name: string; category: string; lat: number; lng: number }>,
    maxDetourKm: number = 20,
    maxDetourPercent: number = 15
  ): Promise<DetourRecommendation[]> {
    const recommendations: DetourRecommendation[] = []

    // Check each POI for proximity to route
    for (let i = 0; i < stages.length - 1; i++) {
      const from = stages[i]
      const to = stages[i + 1]

      const directDistance = this.calculateDistance(
        from.location.coordinates,
        to.location.coordinates
      )

      for (const poi of pois) {
        // Calculate detour distance (from -> POI -> to) vs direct (from -> to)
        const distanceFromToPOI = this.calculateDistance(from.location.coordinates, {
          lat: poi.lat,
          lng: poi.lng,
        })

        const distancePOIToTo = this.calculateDistance(
          { lat: poi.lat, lng: poi.lng },
          to.location.coordinates
        )

        const detourDistance = distanceFromToPOI + distancePOIToTo - directDistance
        const detourPercent = (detourDistance / directDistance) * 100

        // Check if detour is acceptable
        if (detourDistance <= maxDetourKm && detourPercent <= maxDetourPercent) {
          const detourMinutes = (detourDistance / 90) * 60 // Assume 90 km/h avg

          recommendations.push({
            poiId: poi.id,
            name: poi.name,
            category: poi.category,
            location: { lat: poi.lat, lng: poi.lng },
            insertAfterStageId: from.id,
            detourKm: Math.round(detourDistance * 10) / 10,
            detourMinutes: Math.round(detourMinutes),
            detourPercent: Math.round(detourPercent * 10) / 10,
            score: this.calculateDetourScore(detourDistance, detourPercent, poi.category),
            reason: `Détour de ${Math.round(detourDistance)} km (${Math.round(detourPercent)}%) sur votre trajet`,
          })
        }
      }
    }

    // Sort by score (best first) and return top 10
    const sorted = [...recommendations]
    sorted.sort((a, b) => b.score - a.score)
    return sorted.slice(0, 10)
  }

  /**
   * Calculate detour relevance score (0-100)
   */
  private calculateDetourScore(detourKm: number, detourPercent: number, category: string): number {
    let score = 100

    // Penalize longer detours
    score -= detourKm * 2
    score -= detourPercent * 1.5

    // Bonus for interesting categories
    const bonuses: Record<string, number> = {
      nature: 10,
      vue: 10,
      musée: 8,
      gastronomie: 7,
      monument: 6,
    }

    score += bonuses[category] || 0

    return Math.max(0, Math.min(100, Math.round(score)))
  }

  /**
   * Compare different driving profiles
   */
  async compareProfiles(
    stages: Stage[],
    profiles: DrivingProfile[],
    constraints: RouteConstraints
  ): Promise<Array<{ profile: DrivingProfile; metrics: RouteMetrics; estimatedArrival: string }>> {
    const comparisons = []

    for (const profileId of profiles) {
      const profile = DRIVING_PROFILES[profileId]
      const metrics = this.calculateRouteMetrics(stages, profile, constraints)

      // Calculate estimated arrival (assuming start now)
      const now = new Date()
      const arrivalDate = new Date(now.getTime() + metrics.totalDurationHours * 60 * 60 * 1000)

      comparisons.push({
        profile: profileId,
        metrics,
        estimatedArrival: arrivalDate.toISOString(),
      })
    }

    return comparisons
  }

  /**
   * Haversine distance formula (great-circle distance)
   * Returns distance in kilometers
   */
  private calculateDistance(
    from: { lat: number; lng: number },
    to: { lat: number; lng: number }
  ): number {
    const R = 6371 // Earth's radius in km
    const dLat = this.toRad(to.lat - from.lat)
    const dLng = this.toRad(to.lng - from.lng)

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(from.lat)) *
      Math.cos(this.toRad(to.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  private toRad(degrees: number): number {
    return (degrees * Math.PI) / 180
  }
}

export const routeOptimizationService = new RouteOptimizationService()
